import { useRef, useState } from 'react';
import type { ChangeEvent, VoidFunctionComponent } from 'react';
import { Modal } from 'react-bootstrap';
import * as _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import Button, { BUTTON_SIZE, BUTTON_TYPE } from '../../../../../generic/Button';
import * as ReportGenerationStore from '../../../../../../redux/reportGeneration';
import OrganizationsModule from '../../../../../../redux/organizations';
import Flex from '../../../../../generic/Flex';
import styles from '../SidePanel.module.less';
import Loader from '../../../../../generic/Loader';
import { useToggle } from '../../../../../../hooks';
import { window } from '../../../../../../globals';
import { AccessGate, icons } from '../../../../../generic';
import TextArea from '../../../../../generic/Forms/TextArea';
import Locator from '../../../../../../../shared/Locator';
import Toggle from '../../../../../generic/Toggle';
import PropagateHeaderChangesModal from './PropagateHeaderChangesModal';
import { Action, ResourceType } from '../../../../../../../typings/permissions';
import { useForceReportReRender } from '../../hooks';
import { ProjectType } from '../../utils';
import Markdown from '../../../../../generic/Markdown';

const DefaultImageUrl = '/img/fossa_flat_dark.svg';
const ImageUrl = '/organization/logo';

enum ImageSaveAction {
  Save = 'save',
  Remove = 'remove',
}

// Eslint doesn't like this "should"
// eslint-disable-next-line @typescript-eslint/naming-convention
const SHOULD_SET_DEFAULT = false;
// eslint-disable-next-line @typescript-eslint/naming-convention
const SHOULD_PROPAGATE = false;

type Props = {
  shouldShowModal: boolean;
  onHide(): void;
};

/**
 * This modal provides an interface to modify custom headers: logo and text
 * It will also provide an option to propagate changes to all reports
 * Note - this modal contains three separate pieces of functionality in one UI:
 * 1. Set custom header image
 * 2. Set custom header text
 * 3. Propagate changes to all reports
 */
const CustomHeaderModal: VoidFunctionComponent<Props> = ({ shouldShowModal, onHide }) => {
  const dispatch = useDispatch();
  const forceReportReRender = useForceReportReRender();
  const inputRef = useRef<HTMLInputElement>(null);
  const [saveAction, setSaveAction] = useState<ImageSaveAction | null>(null);
  const [shouldOpenPropagationModal, setShouldOpenPropagationModal] = useToggle();
  const [shouldShowSetDefaultModal, toggleShouldShowSetDefaultModal] = useToggle(false);
  const [error, setError] = useState<string>('');
  // Image state
  const [imageUrl, setImageUrl] = useState<string>(window.user?.organization?.image ? ImageUrl : DefaultImageUrl);
  const [imageFile, setImageFile] = useState<File>();
  // Text state
  const [shouldShowPreview, toggleShouldShowPreview] = useToggle(false);
  const projectType = useSelector(ReportGenerationStore.selectors.getProjectType);
  const reportContext = useSelector(ReportGenerationStore.selectors.getReportContext);
  const customText = useSelector(ReportGenerationStore.selectors.getCustomHeaderText);

  // Other state
  const isLoading = useSelector(ReportGenerationStore.selectors.getCustomerHeaderLoading);
  const canUserSetDefault = window.hasPermission({
    action: Action.Edit,
    resourceType: ResourceType.OrganizationGeneralSettings,
  });

  // Handlers
  const setCustomText = (text: string) => dispatch(ReportGenerationStore.actions.setCustomHeaderText(text));

  const handleSelectImage = (event: ChangeEvent<HTMLInputElement>) => {
    const file: File | undefined = _.head(event.target.files);

    if (file) {
      const fileUrl = URL.createObjectURL(file);
      setImageUrl(fileUrl);
      setImageFile(file);
      setSaveAction(ImageSaveAction.Save);
    }
  };

  const handleRemoveImage = () => {
    setImageUrl(DefaultImageUrl);
    setSaveAction(ImageSaveAction.Remove);
  };

  // Save functionality
  const handleSave = async (shouldSetDefault: boolean, shouldPropagate: boolean) => {
    if (!window.user) {
      return;
    }

    try {
    // Update image, as applicable
      if (saveAction === ImageSaveAction.Save && imageFile) {
        await dispatch(ReportGenerationStore.actions.uploadLogo(imageFile));
        window.user.organization.image = imageFile.name;
      } else if (saveAction === ImageSaveAction.Remove) {
        await dispatch(ReportGenerationStore.actions.removeLogo());
        window.user.organization.image = undefined;
      }

      if (shouldSetDefault) {
        await dispatch(
          OrganizationsModule.actions.updateDefaultReportCustomText(
            window.user.organizationId, { customText, shouldPropagate },
          ),
        );
      }

      if (projectType === ProjectType.Project && 'revision' in reportContext) {
        const project = Locator.parsePackageString(reportContext.revision);
        await dispatch(
          ReportGenerationStore.actions.updateProjectCustomHeaderText(project, customText),
        );
      } else if (projectType === ProjectType.ReleaseGroup && 'groupId' in reportContext) {
        await dispatch(
          ReportGenerationStore.actions.updateGroupCustomHeaderText(
            reportContext.groupId, customText,
          ),
        );
      }
    } catch (err: any) {
      setError(err.message);
    }

    forceReportReRender();

    onHide();
  };

  const handleSubmit = async () => {
    if (shouldOpenPropagationModal) {
      return toggleShouldShowSetDefaultModal();
    }

    return handleSave(SHOULD_SET_DEFAULT, SHOULD_PROPAGATE);
  };

  return (
    <div className={styles.reportCustomHeaderModal}>
      <Modal show={shouldShowModal} onHide={onHide}>
        <Modal.Header closeButton>
          <h2>Customize Report Header</h2>
        </Modal.Header>
        <Modal.Body className={styles.reportCustomHeaderModal}>
          <AccessGate>
            {error && <div className="alert alert-error">{error}</div>}
            <div className={styles.reportCustomHeaderModalLogo}>
              {/* Custom Logo Section */}
              <div>
                <h4>Logo</h4>
                <span>
                  Upload any PNG or JPEG image to use as a logo within HTML, Markdown and PDF reports.
                </span>
              </div>
              {imageUrl && <img src={imageUrl} alt="Custom Logo" width="100px" />}
              <Flex direction="column" justify="center" gap="10px">
                <Button
                  onClick={() => inputRef?.current?.click()}
                  type={BUTTON_TYPE.SECONDARY}
                  size={BUTTON_SIZE.MEDIUM}
                  isFullWidth
              >
                  Select Logo
                </Button>
                <Button
                  onClick={handleRemoveImage}
                  type={BUTTON_TYPE.SECONDARY}
                  size={BUTTON_SIZE.MEDIUM}
                  isDisabled={imageUrl === DefaultImageUrl}
                  isFullWidth
              >
                  Remove Logo
                </Button>
              </Flex>
              <input
                className={styles.imageInput}
                type="file"
                hidden
                ref={inputRef}
                accept="image/png,image/jpeg"
                onChange={handleSelectImage}
          />
            </div>
            {/* Custom Text Section */}
            <div>
              <h4>Description</h4>
              {shouldShowPreview ? (
                <Markdown text={customText} className={styles.reportCustomHeaderModalTextPreview} />
              ) : (
                <TextArea
                  placeholder="**Markdown** text is supported"
                  label="Custom header text"
                  value={customText}
                  onChange={setCustomText}
                  autoFocus
            />
              )}
            </div>
            {/* Save changes Button */}
          </AccessGate>
        </Modal.Body>
        <Modal.Footer className={styles.reportCustomHeaderModalFooter}>
          <AccessGate behavior="hide">
            {canUserSetDefault && (
              <div>
                <Toggle
                  checked={shouldOpenPropagationModal}
                  onChange={setShouldOpenPropagationModal}
                  />
                <span>Make default</span>
              </div>
            )}
            <div>
              <Button
                onClick={toggleShouldShowPreview}
                type="outline"
                size="medium"
                icon={shouldShowPreview ? icons.pencil : icons.eye}
              >
                {shouldShowPreview ? 'Edit' : 'Preview text'}
              </Button>
              <Button
                onClick={handleSubmit}
                type="primary"
                size="medium"
              >
                {isLoading ? (<Loader size={15} />) : (<span>Save Changes</span>)}
              </Button>
            </div>
          </AccessGate>
        </Modal.Footer>
      </Modal>
      <PropagateHeaderChangesModal
        shouldShowModal={shouldShowSetDefaultModal}
        onHide={toggleShouldShowSetDefaultModal}
        onSave={handleSave}
      />
    </div>
  );
};

export default CustomHeaderModal;
